<template>
  <div class="w-full max-w-[80vw]">
    <div class="flex flex-col min-w-[22rem] w-full">
      <div class="relative focus-within:z-10">
        <s-input
          v-model="filterText"
          placeholder="Filter Options"
          class="w-full !rounded-b-none !border-b-0 pr-8"
        />
        <s-icon
          name="magnify"
          class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 transition ease-out duration-100 mt-px"
          :class="[filterText ? 'opacity-0' : 'opacity-70']"
        />
      </div>
      <div class="max-h-60 overflow-auto border border-gray-200 scroll-shadow">
        <div
          v-if="filteredOptions.length === 0"
          class="py-2 px-4 text-left bg-gray-100 text-gray-500 whitespace-nowrap"
        >
          {{ $t('filter.noResults') }}
        </div>
        <ul v-else>
          <li v-for="(option, index) in filteredOptions">
            <label
              :key="getOptionValue(option)"
              :data-option-index="index"
              class="group relative flex items-center transition-colors ease-out duration-100 py-1.5 px-2 border-y hover:border-blue-200/20 hover:bg-blue-200/20 cursor-pointer -mb-px select-none focus-within:border-blue-200/20 focus-within:bg-blue-200/20"
              :class="[
                isOptionSelectedTemp(option)
                  ? 'bg-blue-200/20 border-blue-200/20'
                  : 'border-transparent',
              ]"
              :for="`option-${getOptionValue(option)}`"
            >
              <div
                class="shrink-0 relative grid grid-cols-1 grid-rows-1 justify-center items-center rounded mr-2"
              >
                <input
                  :id="`option-${getOptionValue(option)}`"
                  class="peer z-[-1] absolute block w-full h-full row-start-1 col-start-1 opacity-0 border-transparent outline-none"
                  type="checkbox"
                  :checked="isOptionSelectedTemp(option)"
                  @change="toggleOptionTemp(option)"
                  @keydown.arrow-down.prevent="focusNextOption(index)"
                  @keydown.arrow-up.prevent="focusPreviousOption(index)"
                  tabindex="0"
                  :aria-label="getOptionLabel(option)"
                />
                <div
                  class="row-start-1 col-start-1 grid grid-cols-1 grid-rows-1 justify-center items-center peer-focus:outline peer-focus:outline-2 peer-focus:outline-offset-2 rounded-full peer-focus:outline-blue-400"
                  role="presentation"
                >
                  <s-icon
                    name="circle-outline"
                    class="row-start-1 col-start-1 transition-all ease-out duration-150 -m-0.5"
                    :class="{
                      'opacity-0': isOptionSelectedTemp(option),
                      'opacity-100 group-hover:opacity-70 text-gray-300 group-hover:text-blue-400':
                        !isOptionSelectedTemp(option),
                    }"
                  />
                  <s-icon
                    name="check-circle"
                    class="row-start-1 col-start-1 transition-all ease-out duration-150 -m-0.5"
                    :class="{
                      'opacity-100 group-hover:opacity-70 text-blue-600':
                        isOptionSelectedTemp(option),
                      'opacity-0': !isOptionSelectedTemp(option),
                    }"
                  />
                </div>
              </div>
              <slot name="option" v-bind="{option}">
                {{ getOptionLabel(option) }}
              </slot>
            </label>
          </li>
        </ul>
      </div>
      <s-btn
        class="!rounded-t-none !rounded-b-lg focus:z-10"
        @click.stop="applySelection"
        icon="filter"
      >
        {{ $t('filter.apply') }}
      </s-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, computed, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import SIcon from './SIcon.vue';
import SInput from './SInput.vue';
import SBtn from './SBtn.vue';

const props = defineProps<{
  options: any[];
  modelValue: any[];
  labelKey?: string;
  valueKey?: string;
}>();

const emit = defineEmits(['update:modelValue', 'close']);

const filterText = ref('');
const selectedOptions = ref<any[]>([]);
const tempSelections = ref<any[]>([]);

// Map selected values to actual option objects on initial load
watch(
  () => props.modelValue,
  (newValues) => {
    if (Array.isArray(newValues)) {
      const selected = props.options.filter((option) => newValues.includes(getOptionValue(option)));
      selectedOptions.value = selected;
      tempSelections.value = [...selected]; // Initialize temp selections
    } else {
      selectedOptions.value = [];
      tempSelections.value = [];
    }
  },
  {immediate: true}
);

// Filtered options based on search text
const filteredOptions = computed(() => {
  if (!filterText.value) return props.options;

  return props.options.filter((option) => {
    const label = getOptionLabel(option).toLowerCase();
    return label.includes(filterText.value.toLowerCase());
  });
});

function getOptionValue(option: any): any {
  return props.valueKey ? option[props.valueKey] : option;
}

function getOptionLabel(option: any): string {
  return props.labelKey ? option[props.labelKey] : option.toString();
}

function isOptionSelected(option: any): boolean {
  return selectedOptions.value.some(
    (selected) => getOptionValue(selected) === getOptionValue(option)
  );
}

function isOptionSelectedTemp(option: any): boolean {
  return tempSelections.value.some(
    (selected) => getOptionValue(selected) === getOptionValue(option)
  );
}

// Toggle selection in the temporary selections only
function toggleOptionTemp(option: any): void {
  if (isOptionSelectedTemp(option)) {
    tempSelections.value = tempSelections.value.filter(
      (selected) => getOptionValue(selected) !== getOptionValue(option)
    );
  } else {
    tempSelections.value.push(option);
  }
}

// Keyboard navigation functions
function focusNextOption(currentIndex: number): void {
  const nextIndex = currentIndex + 1;
  if (nextIndex < filteredOptions.value.length) {
    const nextOption = document.querySelector(`[data-option-index="${nextIndex}"]`) as HTMLElement;
    if (nextOption) {
      nextOption.focus();
    }
  }
}

function focusPreviousOption(currentIndex: number): void {
  const prevIndex = currentIndex - 1;
  if (prevIndex >= 0) {
    const prevOption = document.querySelector(`[data-option-index="${prevIndex}"]`) as HTMLElement;
    if (prevOption) {
      prevOption.focus();
    }
  }
}

// Apply selections when the user clicks the Apply button
function applySelection(): void {
  selectedOptions.value = [...tempSelections.value];
  emit('update:modelValue', selectedOptions.value.map(getOptionValue));
  emit('close');
}

// Handle close event to revert temporary selections
function handleClose(): void {
  tempSelections.value = [...selectedOptions.value];
  emit('close');
}

const {t} = useI18n({
  inheritLocale: true,
  useScope: 'global',
});
</script>
